import { Button, MonetaryAmount, Shimmer } from '@components'
import { useBusinessContext } from '@context'
import { IDocumentRelation, IRelatedDocumentRole, IRelationType } from '@query/documentRelations'
import { useDocument } from '@root/query/hooks/documents'
import { formatDate } from '@utils'
import { AnimatePresence } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowRight, FaCheck, FaFileInvoice, FaTimes } from 'react-icons/fa'
import { FaCircleCheck } from 'react-icons/fa6'
import { useHistory } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { DocumentRelationPreview } from './DocumentRelationPreview'
import {
  MutedButtonGroup,
  RelationContent,
  RelationIcon,
  RelationItem,
  RelationMapperThemeMode,
  RelationRowPrimary,
  RelationRowSecondary
} from './RelationMapper.styled'
import { getCounterpartRole, getRelationRowAmount } from './relationMapperUtils'

export interface DocumentRelationFormValue extends Omit<IDocumentRelation, 'id' | 'description'> {
  id?: number
  _optimisticId?: string
  description?: string
}

export const createRelationKey = (relation: DocumentRelationFormValue): string => {
  if (relation.id != null) {
    return `existing-${relation.id}`
  }

  if (relation._optimisticId) {
    return relation._optimisticId
  }

  return `temp-${relation.related_document}-${relation.type}-${relation.role}`
}

const getRelationIconPresentation = (
  counterpartRole: IRelatedDocumentRole,
  theme: ReturnType<typeof useTheme>
): { icon: React.ReactNode; color: string } => ({
  icon: counterpartRole === IRelatedDocumentRole.ACCRUAL ? <FaFileInvoice /> : <FaCircleCheck />,
  color:
    counterpartRole === IRelatedDocumentRole.SETTLEMENT
      ? theme.colors.success
      : theme.colors.dashboardNeg
})

const formatRelationDate = (date?: string | null): string => (date ? formatDate(date) : '')

type RelationRowSource = Pick<
  DocumentRelationFormValue,
  'related_document' | 'role' | 'type' | 'related_document_summary'
>

const useRelationRowPresentation = (source: RelationRowSource) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const summary = source.related_document_summary
  const { data: relatedDocument, isLoading } = useDocument(
    {
      documentId: source.related_document
    },
    {
      staleTime: 1000 * 60 * 5,
      enabled: !summary
    }
  )

  const counterpartRole = getCounterpartRole(source.role, source.type)
  const { icon: relationIcon, color: relationIconColor } = getRelationIconPresentation(
    counterpartRole,
    theme
  )
  const rowDate = summary?.date ?? relatedDocument?.date ?? null
  const rowContact =
    summary?.contact_name ?? relatedDocument?.contact_name ?? relatedDocument?.description ?? null
  const rowAmount = getRelationRowAmount(source, relatedDocument)
  const formattedDate = formatRelationDate(rowDate)
  let rowTitle: string
  switch (source.type) {
    case IRelationType.SALARY_PAYMENT:
      rowTitle = t(`documentRelations.role.SALARY_PAYMENT.${counterpartRole}`, {
        date: formattedDate
      })
      break
    case IRelationType.ACCRUAL_PAIR:
      rowTitle = t(`documentRelations.roles.${counterpartRole}.rowTitle`, {
        date: formattedDate
      })
      break
    default: {
      const exhaustive: never = source.type
      rowTitle = exhaustive
    }
  }

  return {
    relationIcon,
    relationIconColor,
    rowTitle,
    rowAmount,
    rowContact,
    isLoading,
    hasSummary: Boolean(summary)
  }
}

const RelationRowMetaContent: React.FC<{
  themeMode: RelationMapperThemeMode
  isLoading: boolean
  hasSummary: boolean
  rowAmount: number | null
  rowContact: string | null
}> = ({ themeMode, isLoading, hasSummary, rowAmount, rowContact }) => {
  const theme = useTheme()

  return (
    <AnimatePresence mode="wait">
      {isLoading && !hasSummary ? (
        <Shimmer
          height="0.75rem"
          width="60%"
          backgroundColor={
            themeMode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : theme.colors.neutralWhite
          }
        />
      ) : (
        <RelationRowSecondary>
          {rowAmount != null ? (
            <>
              <MonetaryAmount value={rowAmount} />
              {rowContact ? <span>·</span> : null}
            </>
          ) : null}
          {rowContact ? <span>{rowContact}</span> : null}
        </RelationRowSecondary>
      )}
    </AnimatePresence>
  )
}

const RelationRowBody: React.FC<{
  relationIcon: React.ReactNode
  relationIconColor: string
  themeMode: RelationMapperThemeMode
  rowTitle: string
  isLoading: boolean
  hasSummary: boolean
  rowAmount: number | null
  rowContact: string | null
}> = ({
  relationIcon,
  relationIconColor,
  themeMode,
  rowTitle,
  isLoading,
  hasSummary,
  rowAmount,
  rowContact
}) => (
  <>
    <RelationIcon $color={relationIconColor}>{relationIcon}</RelationIcon>
    <RelationContent>
      <RelationRowPrimary $themeMode={themeMode}>{rowTitle}</RelationRowPrimary>
      <RelationRowMetaContent
        themeMode={themeMode}
        isLoading={isLoading}
        hasSummary={hasSummary}
        rowAmount={rowAmount}
        rowContact={rowContact}
      />
    </RelationContent>
  </>
)

export const RelationListItem: React.FC<{
  relation: DocumentRelationFormValue
  onRemove: (relation: DocumentRelationFormValue) => void
  readOnly?: boolean
  onApply?: () => void
  applyLabel?: string
  isApplying?: boolean
  themeMode: RelationMapperThemeMode
  disableRelationPreview?: boolean
}> = ({
  relation,
  onRemove,
  readOnly = false,
  onApply,
  applyLabel,
  isApplying = false,
  themeMode,
  disableRelationPreview = false
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const history = useHistory()
  const theme = useTheme()
  const {
    relationIcon,
    relationIconColor,
    rowTitle,
    rowAmount,
    rowContact,
    isLoading,
    hasSummary
  } = useRelationRowPresentation(relation)

  const documentUrl = `/${slug}/documents?id=${relation.related_document}`

  const rowBody = (
    <RelationRowBody
      relationIcon={relationIcon}
      relationIconColor={relationIconColor}
      themeMode={themeMode}
      rowTitle={rowTitle}
      isLoading={isLoading}
      hasSummary={hasSummary}
      rowAmount={rowAmount}
      rowContact={rowContact}
    />
  )

  const previewBody = (
    <DocumentRelationPreview
      documentId={relation.related_document}
      summary={relation.related_document_summary}
      disabled={disableRelationPreview}
    >
      {rowBody}
    </DocumentRelationPreview>
  )

  if (readOnly) {
    return (
      <RelationItem $themeMode={themeMode}>
        {previewBody}
        <MutedButtonGroup $themeMode={themeMode}>
          {onApply ? (
            <Button
              type="button"
              isSecondary
              size="sm"
              icon={<FaCheck color={theme.colors.success} />}
              aria-label={applyLabel}
              onClick={event => {
                event.stopPropagation()
                onApply()
              }}
              disabled={isApplying}
              showSpinner={isApplying}
            >
              {t('documentRelations.suggestions.link')}
            </Button>
          ) : (
            <Button
              type="button"
              isSecondary
              size="sm"
              icon={<FaArrowRight />}
              aria-label={t('documentRelations.openDocument')}
              onClick={event => {
                event.stopPropagation()
                history.push(documentUrl)
              }}
            />
          )}
        </MutedButtonGroup>
      </RelationItem>
    )
  }

  return (
    <RelationItem $themeMode={themeMode}>
      {previewBody}
      <MutedButtonGroup $themeMode={themeMode}>
        <Button
          type="button"
          isSecondary
          size="sm"
          icon={<FaTimes />}
          aria-label={t('documentRelations.remove')}
          onClick={() => onRemove(relation)}
        />
      </MutedButtonGroup>
    </RelationItem>
  )
}
